<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'

import ActionButton from './ActionButton.vue'
import CheckboxCards from './CheckboxCards.vue'
import ModeToggle from './ModeToggle.vue'
import ModalDrawer from './ModalDrawer.vue'
import RangeSlider from './RangeSlider.vue'
import ValueSlider from './ValueSlider.vue'
import {
  fetchPanelMatches,
  fetchPanelTypes,
  fetchProductDetails,
  navigateToDownload,
} from '../lib/api'
import { resolveMediaUrl, resolveWidgetAsset } from '../lib/assets'
import {
  readDefaultsOverrideFromLocation,
  resolveCalculatorDefaults,
  resolveSystemTemps,
  resolveTargetWattage,
} from '../lib/calculator-defaults'
import { trackEvent } from '../lib/tracking'

const failedImages = ref(new Set())
const isEmbedded = ref(false)

onMounted(() => {
  const el = getCurrentInstance()?.proxy?.$el
  isEmbedded.value = el?.getRootNode() instanceof ShadowRoot
})

const props = defineProps({
  productId: {
    type: String,
    default: '',
  },
  lang: {
    type: String,
    default: '',
  },
  product: {
    type: Object,
    default: null,
  },
  category: {
    type: String,
    default: '',
  },
  market: {
    type: String,
    default: 'UKMyson',
  },
  strings: {
    type: Object,
    required: true,
  },
  showEudop: {
    type: [Boolean, String],
    default: false,
  },
  hideColumns: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['product-not-found', 'download-selected'])

const getProductIdFromUrl = () => {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  return params.get('productId') || params.get('product-id') || ''
}

const resolvedProductId = computed(() => props.productId || getProductIdFromUrl())
const typeOptions = ref([])
const includedTypes = ref([])
const showEudop = computed(
  () => props.showEudop === true || props.showEudop === 'true' || props.showEudop === ''
)
const hiddenColumns = computed(() => {
  if (!props.hideColumns) return new Set()
  return new Set(
    props.hideColumns
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  )
})
const isColumnVisible = (key) => !hiddenColumns.value.has(key)
const heightBounds = ref({ min: 300, max: 900 })
const lengthBounds = ref({ min: 400, max: 3000 })
const isLoadingTypes = ref(false)
const loadTypesError = ref('')
const matchResults = ref([])
const isMatching = ref(false)
const matchError = ref('')
const productDetails = ref(null)
const calculationKind = ref('')
// Declared before loadTypes: the immediate watch below runs it during setup, and it resets these
// before its first await
const DEFAULT_TEMP_BOUNDS = {
  heating: { flowMin: 30, flowMax: 110, returnMin: 30, returnMax: 110, roomMin: 7, roomMax: 30 },
  cooling: { flowMin: 5, flowMax: 50, returnMin: 15, returnMax: 50, roomMin: 7, roomMax: 30 },
}
const tempBounds = ref(structuredClone(DEFAULT_TEMP_BOUNDS))
const showTypeOptions = computed(() => typeOptions.value.length > 1)
const getTypeKey = (row) => row.type ?? 'unknown' // row.variant ?? 'unknown'
const normalizeGroupLabel = (label) => {
  if (label == null || label === '') return 'unknown'
  const normalized = String(label)
  return normalized === 'ungrouped' ? 'unknown' : normalized
}
const isUnknownType = (label) => normalizeGroupLabel(label) === 'unknown'

const groupedResults = computed(() => {
  const raw = matchResults.value
  if (!Array.isArray(raw) || raw.length === 0) return []

  const looksGrouped = raw.every(
    (entry) => entry && typeof entry === 'object' && Array.isArray(entry.items)
  )
  if (looksGrouped) {
    return raw.map((entry) => [normalizeGroupLabel(entry.label), entry.items ?? []])
  }

  const groups = {}
  raw.forEach((row) => {
    const key = normalizeGroupLabel(getTypeKey(row))
    if (!groups[key]) groups[key] = []
    groups[key].push(row)
  })
  return Object.entries(groups)
})
const modeTypePattern = /^\d\d\-(boost|standby|comfort)\-(heating|cooling)$/
const resultsGroupMeta = computed(() => {
  const meta = {}
  groupedResults.value.forEach(([type]) => {
    const match = modeTypePattern.exec(type)
    if (match) {
      meta[type] = { mode: match[1], thermal: match[2] }
    }
  })
  return meta
})
const totalResults = computed(() => {
  const raw = matchResults.value
  if (!Array.isArray(raw) || raw.length === 0) return 0
  const looksGrouped = raw.every(
    (entry) => entry && typeof entry === 'object' && Array.isArray(entry.items)
  )
  if (looksGrouped) {
    return raw.reduce((sum, entry) => sum + (entry.items?.length ?? 0), 0)
  }
  return raw.length
})

const capitalize = (str) => {
  if (typeof str !== 'string' || str.length === 0) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}
const formatCountString = (template, count) => {
  if (!template) return String(count)
  return template.replace('{count}', count)
}
const getRowKey = (row, index, groupType) => {
  const base =
    row?.itemId ||
    row?.hvacWaterCode ||
    row?.hvacElectricCode ||
    row?.localItemCode ||
    row?.globalItemCode ||
    `row-${index}`
  return `${groupType}-${base}-${index}`
}
const selectedRowKeys = ref([])
const isRowSelected = (key) => selectedRowKeys.value.includes(key)
const toggleRowSelection = (key) => {
  const next = new Set(selectedRowKeys.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  selectedRowKeys.value = Array.from(next)
}

const toTypeOption = (item) => {
  if (item == null) return null
  if (typeof item === 'string' || typeof item === 'number') {
    const id = String(item)
    return { id, label: id }
  }

  if (typeof item === 'object') {
    const id = item.id ?? item.code ?? item.value ?? item.type ?? item.name
    if (id == null) return null
    const label = item.label ?? item.name ?? item.title ?? String(id)
    return { id: String(id), label: String(label) }
  }

  return null
}

let typesRequestId = 0
const loadTypes = async () => {
  const requestId = ++typesRequestId
  if (!resolvedProductId.value) {
    typeOptions.value = []
    includedTypes.value = []
    isLoadingTypes.value = false
    return
  }

  isLoadingTypes.value = true
  loadTypesError.value = ''
  tempBounds.value = structuredClone(DEFAULT_TEMP_BOUNDS)
  try {
    const data = await fetchPanelTypes(props.market, resolvedProductId.value, props.lang)
    // Another radiator was picked while this request was in flight
    if (requestId !== typesRequestId) return
    const list = Array.isArray(data) ? data : (data?.types ?? [])
    const mapped = list.map(toTypeOption).filter(Boolean)
    typeOptions.value = mapped
    includedTypes.value = mapped.map((type) => type.id)
    if (Number.isFinite(data?.minHeight) && Number.isFinite(data?.maxHeight)) {
      heightBounds.value = { min: data.minHeight, max: data.maxHeight }
      heightRange.value = { min: data.minHeight, max: data.maxHeight }
    }
    if (Number.isFinite(data?.minLength) && Number.isFinite(data?.maxLength)) {
      lengthBounds.value = { min: data.minLength, max: data.maxLength }
      lengthRange.value = { min: data.minLength, max: data.maxLength }
    }
    // Always start from the defaults so one product's limits (e.g. the Ulow series) never carry
    // over to the next. The API only sends a cooling block for cooling-capable products.
    const mergeBounds = (defaults, fromApi) =>
      Object.fromEntries(
        Object.entries(defaults).map(([key, fallback]) => [
          key,
          Number.isFinite(fromApi?.[key]) ? fromApi[key] : fallback,
        ])
      )
    tempBounds.value = {
      heating: mergeBounds(DEFAULT_TEMP_BOUNDS.heating, data?.heating),
      cooling: mergeBounds(DEFAULT_TEMP_BOUNDS.cooling, data?.cooling),
    }
    if (typeof data?.calculation === 'string') {
      calculationKind.value = data.calculation
    } else {
      calculationKind.value = ''
    }
    productMedianWattage.value = Number.isFinite(data?.medianWattage) ? data.medianWattage : null
    // Reset to heating mode and defaults when switching to a non-cooling radiator
    const supportsCooling =
      calculationKind.value === 'THOP - Ulow E2 - Horizontal for heating and cooling' ||
      calculationKind.value === 'THOP - Ulow E2 - Vertical for heating and cooling'
    if (!supportsCooling) {
      modeValue.value = 'heating'
      const bounds = tempBounds.value.heating
      flowTempSlider.value = clampTemp(defaultTemps.heating.flow, bounds.flowMin, bounds.flowMax)
      returnTempSlider.value = clampTemp(
        defaultTemps.heating.return,
        bounds.returnMin,
        bounds.returnMax
      )
    }
  } catch (error) {
    if (requestId !== typesRequestId) return
    loadTypesError.value = error?.message ?? props.strings.calc.errorTypes
    typeOptions.value = []
    includedTypes.value = []
    calculationKind.value = ''
    productMedianWattage.value = null
  } finally {
    if (requestId === typesRequestId) isLoadingTypes.value = false
  }
}

const loadProductDetails = async () => {
  if (!resolvedProductId.value) {
    productDetails.value = null
    emit('product-not-found', false)
    return
  }
  try {
    const data = await fetchProductDetails(props.market, resolvedProductId.value, props.lang)
    productDetails.value = data
    const normalized = resolveProductInfo(data)
    const notFound = !normalized
    emit('product-not-found', notFound)
    if (notFound) {
      console.warn(`Product not found for productId: ${resolvedProductId.value}`)
    }
  } catch {
    productDetails.value = null
    console.warn(`Product not found for productId: ${resolvedProductId.value}`)
    emit('product-not-found', true)
  }
}

watch(
  () => [resolvedProductId.value, props.market],
  () => {
    matchResults.value = []
    matchError.value = ''
    selectedRowKeys.value = []
    loadProductDetails()
    loadTypes()
  },
  { immediate: true }
)

watch(
  () => matchResults.value,
  () => {
    selectedRowKeys.value = allRowKeys.value
  }
)

const resolveProductInfo = (value) => {
  if (!value) return null
  if (Array.isArray(value)) return value[0] ?? null
  if (Array.isArray(value?.items)) return value.items[0] ?? null
  if (value?.product) return value.product
  return value
}

const productInfo = computed(() => {
  const details = resolveProductInfo(productDetails.value)
  if (props.product?.imageUrl) return props.product
  if (details?.imageUrl) return details
  return props.product ?? details
})
const formatRounded = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric.toFixed(2) : ''
}
const heightRange = ref({ min: 300, max: 900 })
const lengthRange = ref({ min: 400, max: 3000 })
const toleranceRange = ref({ min: -10, max: 10 })
// Initial heating temperatures depend on the market (e.g. AustriaVN starts at 75/65/20)
const marketTemps = computed(() => resolveSystemTemps(props.market))
const flowTempSlider = ref(marketTemps.value.flow)
const returnTempSlider = ref(marketTemps.value.return)
const roomTempSlider = ref(marketTemps.value.room)
const defaultTemps = {
  get heating() {
    return { flow: marketTemps.value.flow, return: marketTemps.value.return }
  },
  cooling: { flow: 7, return: 12 },
}
const clampTemp = (value, min, max) => Math.min(Math.max(value, min), max)
const requiredWattsSlider = ref(1000)
const showDrawer = ref(false)

// Per-category defaults for target wattage and tolerance. The tolerance band is applied whenever
// the resolved category changes (explicit category attribute, or inferred from the calculation
// method when the widget is locked to a product-id), so switching products within one category
// keeps the user's own slider values. The target wattage additionally follows the product's
// median wattage when the API supplies one (electric products), so the first calculation hits.
const defaultsOverride = readDefaultsOverrideFromLocation()
const appliedDefaultsKey = ref('')
const productMedianWattage = ref(null)
const applyCalculatorDefaults = ([, , median] = [], [, , previousMedian] = []) => {
  const defaults = resolveCalculatorDefaults({
    category: props.category,
    calculation: calculationKind.value,
    override: defaultsOverride,
  })
  const keyChanged = defaults.key !== appliedDefaultsKey.value
  const medianChanged = median !== previousMedian
  if (keyChanged) {
    appliedDefaultsKey.value = defaults.key
    toleranceRange.value = { min: defaults.toleranceMin, max: defaults.toleranceMax }
  }
  if (keyChanged || medianChanged) {
    requiredWattsSlider.value = resolveTargetWattage({
      defaults,
      productMedianWattage: median,
      override: defaultsOverride,
    })
  }
}
watch(
  () => [props.category, calculationKind.value, productMedianWattage.value],
  applyCalculatorDefaults,
  { immediate: true }
)
const modeValue = ref('heating')
const modeOptions = computed(() => [
  { value: 'heating', label: props.strings.calc.modes.heating },
  { value: 'cooling', label: props.strings.calc.modes.cooling },
])
const currentTempBounds = computed(() =>
  modeValue.value === 'cooling' ? tempBounds.value.cooling : tempBounds.value.heating
)
const selectedCount = computed(() => selectedRowKeys.value.length)
const selectedRows = computed(() => {
  const selected = new Set(selectedRowKeys.value)
  const rows = []
  groupedResults.value.forEach(([type, items]) => {
    items.forEach((row, index) => {
      const key = getRowKey(row, index, type)
      if (selected.has(key)) rows.push({ type, row })
    })
  })
  return rows
})
const allRowKeys = computed(() => {
  const keys = []
  groupedResults.value.forEach(([type, items]) => {
    items.forEach((row, index) => {
      keys.push(getRowKey(row, index, type))
    })
  })
  return keys
})
const allRowsSelected = computed(() => {
  const keys = allRowKeys.value
  return keys.length > 0 && keys.every((key) => selectedRowKeys.value.includes(key))
})
const toggleAllRows = () => {
  if (allRowsSelected.value) {
    selectedRowKeys.value = []
  } else {
    selectedRowKeys.value = allRowKeys.value
  }
}

watch(
  () => [modeValue.value, currentTempBounds.value],
  ([, bounds]) => {
    flowTempSlider.value = clampTemp(flowTempSlider.value, bounds.flowMin, bounds.flowMax)
    returnTempSlider.value = clampTemp(returnTempSlider.value, bounds.returnMin, bounds.returnMax)
    roomTempSlider.value = clampTemp(roomTempSlider.value, bounds.roomMin, bounds.roomMax)
    if (modeValue.value === 'cooling' && returnTempSlider.value <= flowTempSlider.value) {
      const nextReturn = clampTemp(flowTempSlider.value + 1, bounds.returnMin, bounds.returnMax)
      returnTempSlider.value = nextReturn
    }
    if (modeValue.value === 'heating' && flowTempSlider.value <= returnTempSlider.value) {
      const nextFlow = clampTemp(returnTempSlider.value + 1, bounds.flowMin, bounds.flowMax)
      flowTempSlider.value = nextFlow
    }
  },
  { immediate: true, deep: true }
)

// A different market brings its own starting temperatures
watch(marketTemps, (temps) => {
  if (modeValue.value !== 'heating') return
  const bounds = tempBounds.value.heating
  flowTempSlider.value = clampTemp(temps.flow, bounds.flowMin, bounds.flowMax)
  returnTempSlider.value = clampTemp(temps.return, bounds.returnMin, bounds.returnMax)
  roomTempSlider.value = clampTemp(temps.room, bounds.roomMin, bounds.roomMax)
})

watch(
  () => modeValue.value,
  (mode) => {
    const bounds = currentTempBounds.value
    if (mode === 'cooling') {
      flowTempSlider.value = clampTemp(defaultTemps.cooling.flow, bounds.flowMin, bounds.flowMax)
      returnTempSlider.value = clampTemp(
        defaultTemps.cooling.return,
        bounds.returnMin,
        bounds.returnMax
      )
    } else {
      flowTempSlider.value = clampTemp(defaultTemps.heating.flow, bounds.flowMin, bounds.flowMax)
      returnTempSlider.value = clampTemp(
        defaultTemps.heating.return,
        bounds.returnMin,
        bounds.returnMax
      )
    }
    roomTempSlider.value = clampTemp(roomTempSlider.value, bounds.roomMin, bounds.roomMax)
  }
)

const buildMatchPayload = () => ({
  types: includedTypes.value,
  targetWattage: requiredWattsSlider.value,
  cooling: modeValue.value === 'cooling',
  flowTemp: flowTempSlider.value,
  returnTemp: returnTempSlider.value,
  roomTemp: roomTempSlider.value,
  heightMin: heightRange.value.min,
  lengthMin: lengthRange.value.min,
  heightMax: heightRange.value.max,
  lengthMax: lengthRange.value.max,
  toleranceMin: toleranceRange.value.min,
  toleranceMax: toleranceRange.value.max,
})

const runMatch = async () => {
  if (!resolvedProductId.value) {
    matchError.value = props.strings.calc.selectPanel
    matchResults.value = []
    return
  }

  trackEvent('calculator_calculate_click', {
    product_name: productInfo.value?.name,
    market: props.market,
    mode: modeValue.value,
    target_wattage: requiredWattsSlider.value,
  })

  isMatching.value = true
  matchError.value = ''
  try {
    const payload = buildMatchPayload()
    matchResults.value = await fetchPanelMatches(
      props.market,
      resolvedProductId.value,
      payload,
      props.lang
    )
    trackEvent('calculator_results_displayed', {
      result_count: totalResults.value,
      calculation_kind: calculationKind.value,
      product_name: productInfo.value?.name,
      market: props.market,
    })
  } catch (error) {
    matchError.value = error?.message ?? props.strings.calc.errorMatch
    matchResults.value = []
  } finally {
    isMatching.value = false
  }
}

const trackDopDownload = (dopType, row) => {
  trackEvent('calculator_dop_download', {
    dop_type: dopType,
    item_code: row.globalItemCode,
    product_name: productInfo.value?.name,
    market: props.market,
  })
}

const showHydronic = computed(() => calculationKind.value !== 'EOP - Electric Radiators')
const showModeToggle = computed(
  () =>
    calculationKind.value === 'THOP - Ulow E2 - Horizontal for heating and cooling' ||
    calculationKind.value === 'THOP - Ulow E2 - Vertical for heating and cooling'
)

const downloadSelected = () => {
  if (typeof window === 'undefined') return
  trackEvent('calculator_download_click', {
    selected_count: selectedCount.value,
    product_name: productInfo.value?.name,
    market: props.market,
    lang: props.lang,
  })
  const ids = selectedRows.value
    .map(({ row }) => row?.itemId)
    .filter((id) => id != null && id !== '')
    .join(',')
  if (!ids) return
  const productId = resolvedProductId.value
  const lang = props.lang
  emit('download-selected', selectedRows.value)
  navigateToDownload(productId, lang, ids)
}

const openRequiredDrawer = () => {
  trackEvent('calculator_room_click', {
    product_name: productInfo.value?.name,
    current_wattage: requiredWattsSlider.value,
    market: props.market,
  })
  showDrawer.value = true
}
</script>

<template>
  <main class="calc" :class="{ 'calc--standalone': !isEmbedded }">
    <section class="calc__grid">
      <article v-if="showModeToggle" class="calc__panel calc__panel--full-row calc__panel--mode">
        <p class="calc__label">{{ props.strings.calc.modeLabel }}</p>
        <ModeToggle v-model="modeValue" :options="modeOptions" />
      </article>
      <article
        class="calc__panel"
        :class="{
          'calc__panel--span-two':
            showTypeOptions && typeOptions.length > 3 && typeOptions.length <= 6,
          'calc__panel--full-row': showTypeOptions && typeOptions.length > 6,
        }"
      >
        <p class="calc__label">{{ props.strings.calc.radiatorType }}</p>
        <CheckboxCards v-if="showTypeOptions" :options="typeOptions" v-model="includedTypes" />
        <div v-else class="calc__type-image">
          <img
            v-if="productInfo?.imageUrl"
            :src="resolveMediaUrl(productInfo.imageUrl)"
            :alt="productInfo?.name || productInfo?.productId || ''"
          />
        </div>
        <p v-if="isLoadingTypes" class="calc__status">{{ props.strings.calc.loadingTypes }}</p>
        <p v-else-if="loadTypesError" class="calc__status calc__status--error">
          {{ loadTypesError }}
        </p>
      </article>
      <article class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.height }}</p>
        <RangeSlider
          v-model="heightRange"
          :min="heightBounds.min"
          :max="heightBounds.max"
          :label-from="props.strings.slider.from"
          :label-to="props.strings.slider.to"
        />
      </article>
      <article class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.length }}</p>
        <RangeSlider
          v-model="lengthRange"
          :min="lengthBounds.min"
          :max="lengthBounds.max"
          :label-from="props.strings.slider.from"
          :label-to="props.strings.slider.to"
        />
      </article>
      <article v-if="showHydronic" class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.flowTemp }}</p>
        <ValueSlider
          v-model="flowTempSlider"
          :label="props.strings.slider.value"
          unit="C&deg;"
          :min="currentTempBounds.flowMin"
          :max="currentTempBounds.flowMax"
          :step="1"
        />
      </article>
      <article v-if="showHydronic" class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.returnTemp }}</p>
        <ValueSlider
          v-model="returnTempSlider"
          :label="props.strings.slider.value"
          unit="C&deg;"
          :min="currentTempBounds.returnMin"
          :max="currentTempBounds.returnMax"
          :step="1"
        />
      </article>
      <article class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.roomTemp }}</p>
        <ValueSlider
          v-model="roomTempSlider"
          :label="props.strings.slider.value"
          unit="C&deg;"
          :min="currentTempBounds.roomMin"
          :max="currentTempBounds.roomMax"
          :step="1"
        />
      </article>
      <article class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.tolerance }}</p>
        <RangeSlider
          v-model="toleranceRange"
          :label-from="props.strings.slider.from"
          :label-to="props.strings.slider.to"
          unit="%"
          :min="-30"
          :max="30"
          :step="1"
        />
      </article>
      <article class="calc__panel">
        <p class="calc__label">{{ props.strings.calc.requiredWatts }}</p>
        <ValueSlider
          v-model="requiredWattsSlider"
          :label="props.strings.slider.value"
          unit="W"
          :min="50"
          :max="6000"
          :step="10"
        />
        <ActionButton @click="openRequiredDrawer" :variant="'secondary'">
          <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
              <path
                d="M320-240h60v-80h80v-60h-80v-80h-60v80h-80v60h80v80Zm200-30h200v-60H520v60Zm0-100h200v-60H520v60Zm44-152 56-56 56 56 42-42-56-58 56-56-42-42-56 56-56-56-42 42 56 56-56 58 42 42Zm-314-70h200v-60H250v60Zm-50 472q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z"
              />
            </svg>
          </template>
          {{ props.strings.calc.calculateRequiredButton }}
        </ActionButton>
      </article>
    </section>

    <div class="calc__actions">
      <ActionButton @click="runMatch">{{ props.strings.calc.actionButton }}</ActionButton>
    </div>
    <div v-if="!isMatching && !matchError && totalResults" class="calc__results-summary">
      <p class="calc__results-message">
        {{ formatCountString(props.strings.calc.resultsMessage, totalResults) }}
      </p>
      <ActionButton class="calc__download-button" @click="downloadSelected">
        {{ formatCountString(props.strings.calc.downloadLabel, selectedCount) }}
      </ActionButton>
    </div>

    <div class="calc__results">
      <p v-if="isMatching" class="calc__status">{{ props.strings.calc.calculating }}</p>
      <div v-else-if="matchError" class="results-empty results-empty--error">
        {{ matchError }}
      </div>
      <div v-else-if="matchResults.length" class="results-groups">
        <section
          v-for="[type, rows] in groupedResults"
          :key="type"
          class="results-group"
          :class="{ 'results-group--no-header': isUnknownType(type) }"
        >
          <template v-if="resultsGroupMeta[type]">
            <div class="results-group__header">
              <span :class="['results-group__decor', resultsGroupMeta[type].thermal]"></span>
              <span class="results-group__type-label">{{
                props.strings.calc[`type${capitalize(resultsGroupMeta[type].thermal)}`]
              }}</span>
              <div class="results-group__divider"></div>
              <span class="results-group__type-label">{{
                props.strings.calc[resultsGroupMeta[type].mode]
              }}</span>
            </div>
          </template>
          <template v-else-if="!isUnknownType(type)">
            <slot
              name="results-group-header"
              :type="type"
              :rows="rows"
              :label="props.strings.calc.typeGroupLabel"
              :image-src="resolveWidgetAsset(`images/${type}.webp`)"
            >
              <div class="results-group__header">
                <span class="results-group__label">{{ props.strings.calc.typeGroupLabel }}:</span>
                <span class="results-group__value">{{ type }}</span>
                <img
                  v-if="!failedImages.has(type)"
                  class="results-group__image"
                  :src="resolveWidgetAsset(`images/${type}.webp`)"
                  :alt="type"
                  @error="failedImages = new Set([...failedImages, type])"
                />
                <svg
                  v-else
                  class="results-group__image"
                  viewBox="0 0 72 72"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect class="fallback-image__bg" x="1" y="1" width="70" height="70" />
                  <text class="fallback-image__label" x="36" y="40" text-anchor="middle">
                    {{ type }}
                  </text>
                </svg>
              </div>
            </slot>
          </template>
          <div class="results-table-wrap">
            <table class="results-table">
              <thead>
                <tr>
                  <th v-if="isColumnVisible('itemId')" class="results-table__first-col">
                    <input
                      type="checkbox"
                      :checked="allRowsSelected"
                      :aria-label="props.strings.calc.selectAllLabel"
                      @change="toggleAllRows"
                    />
                    <span>{{ props.strings.calc.tableHeaders.itemId }}</span>
                  </th>
                  <th v-if="isColumnVisible('description')">
                    {{ props.strings.calc.tableHeaders.description }}
                  </th>
                  <th v-if="isColumnVisible('calculatedWattage')">
                    {{ props.strings.calc.tableHeaders.calculatedWattage }}
                  </th>
                  <th v-if="isColumnVisible('height')">
                    {{ props.strings.calc.tableHeaders.height }}
                  </th>
                  <th v-if="isColumnVisible('length')">
                    {{ props.strings.calc.tableHeaders.length }}
                  </th>
                  <th v-if="isColumnVisible('depth')">
                    {{ props.strings.calc.tableHeaders.depth }}
                  </th>
                  <th v-if="showHydronic && isColumnVisible('waterContent')">
                    {{ props.strings.calc.tableHeaders.waterContent }}
                  </th>
                  <th v-if="isColumnVisible('weight')">
                    {{ props.strings.calc.tableHeaders.weight }}
                  </th>
                  <th v-if="isColumnVisible('co2Equivalent')">
                    {{ props.strings.calc.tableHeaders.co2Equivalent }}
                  </th>
                  <th v-if="showEudop && isColumnVisible('eudop')">
                    {{ props.strings.calc.tableHeaders.eudop }}
                  </th>
                  <th v-if="isColumnVisible('dop')">
                    {{ props.strings.calc.tableHeaders.dop }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in rows" :key="getRowKey(row, index, type)">
                  <td v-if="isColumnVisible('itemId')" class="results-table__first-col">
                    <input
                      type="checkbox"
                      :checked="isRowSelected(getRowKey(row, index, type))"
                      :aria-label="props.strings.calc.selectItemLabel"
                      @change="toggleRowSelection(getRowKey(row, index, type))"
                    />
                    <span>{{
                      row.hvacElectricCode ||
                      row.hvacWaterCode ||
                      row.localItemCode ||
                      row.globalItemCode
                    }}</span>
                  </td>
                  <td v-if="isColumnVisible('description')">{{ row.shortDescription }}</td>
                  <td
                    v-if="isColumnVisible('calculatedWattage')"
                    class="results-table__cell--numeric"
                  >
                    {{ row.calculatedWattage }}
                  </td>
                  <td v-if="isColumnVisible('height')" class="results-table__cell--numeric">
                    {{ row.heightMm }}
                  </td>
                  <td v-if="isColumnVisible('length')" class="results-table__cell--numeric">
                    {{ row.lengthMm || row.widthMm }}
                  </td>
                  <td v-if="isColumnVisible('depth')" class="results-table__cell--numeric">
                    {{ row.depthMm }}
                  </td>
                  <td
                    v-if="showHydronic && isColumnVisible('waterContent')"
                    class="results-table__cell--numeric"
                  >
                    {{ formatRounded(row.waterContent) }}
                  </td>
                  <td v-if="isColumnVisible('weight')" class="results-table__cell--numeric">
                    {{ formatRounded(row.weight) }}
                  </td>
                  <td v-if="isColumnVisible('co2Equivalent')" class="results-table__cell--numeric">
                    {{ formatRounded(row.co2NumericValue) }}
                  </td>
                  <td v-if="showEudop && isColumnVisible('eudop')" nowrap>
                    <a
                      target="_blank"
                      :href="row.globalItemCode ? `/api/files/dop/en/${row.globalItemCode}` : '#'"
                      @click="trackDopDownload('eu', row)"
                      >DoP
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="dop-link-icon"
                      >
                        <path
                          d="M14 11V14H2V11H0V14C0 15.1 0.9 16 2 16H14C15.1 16 16 15.1 16 14V11H14ZM13 7L11.59 5.59L9 8.17V0H7V8.17L4.41 5.59L3 7L8 12L13 7Z"
                          fill="#00292E"
                        />
                      </svg>
                    </a>
                  </td>
                  <td v-if="isColumnVisible('dop')" nowrap>
                    <a
                      target="_blank"
                      :href="
                        row.globalItemCode
                          ? `/api/files/dop/${row.language}/${row.globalItemCode}`
                          : '#'
                      "
                      @click="trackDopDownload('localized', row)"
                      >DoP
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="dop-link-icon"
                      >
                        <path
                          d="M14 11V14H2V11H0V14C0 15.1 0.9 16 2 16H14C15.1 16 16 15.1 16 14V11H14ZM13 7L11.59 5.59L9 8.17V0H7V8.17L4.41 5.59L3 7L8 12L13 7Z"
                          fill="#00292E"
                        />
                      </svg>
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <div v-else class="results-empty">{{ props.strings.calc.emptyResults }}</div>
    </div>

    <ModalDrawer
      v-model="showDrawer"
      :strings="props.strings"
      :market="props.market"
      @use="requiredWattsSlider = $event"
    />
  </main>
</template>

<style scoped>
.calc {
  width: 100%;
  max-width: 1200px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 3vw, 32px);
}

.calc--standalone {
  margin: clamp(24px, 5vw, 64px) auto;
  padding: 0 var(--space-24) var(--space-64);
}

.calc__header h1 {
  margin: 0;
  font-size: 2.1rem;
  font-weight: 700;
  color: var(--color-petrol-100);
}

.calc__row {
  border-top: 1px solid var(--semantic-border-muted);
  padding-top: var(--space-20);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-24);
}

.calc__row--mode {
  justify-content: center;
  position: relative;
}

.calc__row--mode .calc__label {
  position: absolute;
  left: 0;
}

.calc__label {
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--semantic-text-default);
}

.calc__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: clamp(16px, 3vw, 32px);
}

.calc__panel {
  border-top: 1px solid var(--semantic-border-muted);
  padding-top: var(--space-16);
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
  min-height: 170px;
  min-width: 0;
}

.calc__panel--full-row {
  grid-column: 1 / -1;
}

.calc__panel--span-two {
  grid-column: 1 / span 2;
}

.calc__panel--mode {
  align-items: center;
  text-align: center;
}

.calc__panel :deep(.action-button) {
  align-self: center;
  margin-top: var(--space-32);
}

.calc__panel :deep(.single-slider__value) {
  align-items: center;
}

.calc__panel :deep(.value-box) {
  min-width: 96px;
  text-align: center;
}

.calc__panel :deep(.checkbox-card__icon) {
  background: #eef3ff;
}

.calc__panel :deep(.checkbox-grid) {
  grid-template-columns: repeat(auto-fit, var(--space-96));
  justify-content: center;
}

.calc__panel :deep(.checkbox-card) {
  padding: var(--space-4);
  gap: var(--space-4);
}

.calc__panel :deep(.checkbox-card__tag) {
  font-size: 0.8rem;
}

.calc__type-image {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
}

.calc__type-image img {
  width: 100%;
  max-width: 160px;
  aspect-ratio: 1;
  object-fit: cover;
}

.calc__panel :deep(.slider-row__value-input) {
  padding: var(--space-8);
  min-width: 0;
}

.calc__panel :deep(.slider-row__value-input input) {
  font-size: 0.9rem;
}

.calc__panel :deep(.slider-row__field) {
  align-items: flex-start;
}

.calc__panel :deep(.slider-row__field:last-child) {
  align-items: flex-end;
  text-align: right;
}

.calc__status {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7b7f;
}

.calc__status--error {
  color: #b93838;
}

.calc__actions {
  display: flex;
  justify-content: center;
  margin-top: var(--space-24);
}

.calc__results-summary {
  border-top: 1px solid var(--semantic-border-muted);
  padding-top: var(--space-20);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-24);
}

.calc__results-message {
  margin: 0;
  font-size: 1rem;
  color: var(--color-petrol-100);
}

.calc__download-button {
  margin-top: 0.9rem;
  font-size: 0.9rem;
  white-space: nowrap;
}

.calc__results {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.results-groups {
  display: flex;
  flex-direction: column;
  gap: var(--space-32);
}

.results-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.results-group--no-header {
  margin-top: var(--space-64);
}

.results-group__header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 600;
  color: var(--color-petrol-100);
}

.results-group__decor {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.results-group__label {
  color: #5a6a6e;
}

.results-group__divider {
  height: 24px;
  width: 1px;
  border-left: 1px solid var(--semantic-border-muted);
}

.results-group__type-label {
  font-weight: 600;
  color: var(--color-petrol-100);
}

.results-group__image {
  width: 89px;
  height: 89px;
  /* Fixed square box matching the original 1:1 type images; non-square images are
     contained (letterboxed) rather than cropped or allowed to stretch the row. */
  object-fit: contain;
  background-color: var(--semantic-bg-default);
}

.results-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.results-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
  border-bottom: 1px solid var(--semantic-border-muted);
}

.results-table th,
.results-table td {
  padding: 3px 8px;
  font-weight: 500;
  border-top: 1px solid var(--semantic-border-muted);
}

.results-table__first-col {
  position: sticky;
  left: 0;
  z-index: 1;
  white-space: nowrap;
  text-align: left;
  border-right: 1px solid var(--semantic-border-muted);
}

.results-table__first-col input[type='checkbox'] {
  vertical-align: middle;
  margin-right: 6px;
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-petrol-70);
  /* border-radius: 2px; */
  background: transparent;
  cursor: pointer;
  position: relative;
}

.results-table__first-col input[type='checkbox']:checked {
  background: var(--color-petrol-70);
}

.results-table__first-col input[type='checkbox']:checked::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 0px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.results-table__cell--numeric {
  text-align: right;
}
.results-table th {
  font-weight: 700;
  font-size: 1rem;
  padding: 2rem 0.9rem;
  color: var(--color-petrol-100);
  background: var(--color-grey-300);
}

.results-table tbody tr:nth-child(odd) td {
  background: var(--color-grey-200);
}

.results-table tbody tr:nth-child(odd) td.results-table__first-col {
  background: var(--color-grey-200);
}

.results-table tbody tr:nth-child(even) td.results-table__first-col {
  background: var(--color-grey-300);
}

.results-table th.results-table__first-col {
  background: var(--color-grey-300);
  z-index: 2;
}

.results-empty {
  border: 1px solid var(--semantic-border-muted);
  border-radius: 8px;
  padding: var(--space-32);
  text-align: center;
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--color-petrol-100);
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.results-empty--error {
  color: #b93838;
  border-color: #e3b2b2;
  background: #fff5f5;
}

.results-table tbody tr:nth-child(even) td {
  background: var(--color-grey-300);
}

.results-table svg.dop-link-icon {
  margin-left: var(--space-4);
  width: 16px;
  height: 16px;
}

@media (max-width: 900px) {
  .calc__row--mode {
    justify-content: flex-start;
  }

  .calc__row--mode .calc__label {
    position: static;
  }
}

@media (max-width: 640px) {
  .calc__panel--span-two,
  .calc__panel--full-row {
    grid-column: 1 / -1;
  }

  .calc__panel {
    min-height: 0;
    gap: var(--space-16);
  }

  .calc {
    padding: 0 var(--space-16) var(--space-32);
    gap: var(--space-24);
  }

  .calc__results-summary {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-16);
    text-align: center;
  }

  .results-table {
    font-size: 0.8rem;
    min-width: 600px;
  }

  .results-table th {
    font-size: 0.85rem;
    padding: 1rem 0.5rem;
  }

  .results-table th,
  .results-table td {
    padding: 3px 5px;
  }
}
</style>
